// components/Orders/ComplaintDrawer.jsx
import React, { useState } from "react"
import { Button, Drawer, Space, Typography, Input, Select, message } from "antd"
import { collection, doc, runTransaction, serverTimestamp, writeBatch } from "firebase/firestore"
import { useAuthContext } from "../../hooks/useAuthContext"
import { db } from "../../firebase/config"
import { CloseOutlined } from "@ant-design/icons"

const { Text } = Typography
const { TextArea } = Input

export default function ComplaintDrawer({
  orderData,
  complaintTypes = ["Count", "Quality", "Error", "Other"],
  responsibilities = [
    "Viva",
    "Arsoma",
    "Manager",
    "Cutter",
    "Paper Manager",
    "Designer",
    "Accounting",
    "Other",
  ],
  buttonText = "Complaint",
  disabled = false,
}) {
  const { user } = useAuthContext()

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  // form fields
  const [type, setType] = useState(complaintTypes[0] || "Other")
  const [responsibility, setResponsibility] = useState(responsibilities[0] || "Other")
  const [comment, setComment] = useState("")

  const orderId = orderData?.id
  const orderNumber =
    orderData?.orderNumber ||
    `${orderData?.orderNumberPrefix || ""}${orderData?.justOrderNumber || ""}` ||
    orderId

  const resetForm = () => {
    setType(complaintTypes[0] || "Other")
    setResponsibility(responsibilities[0] || "Other")
    setComment("")
  }

  const handleOpen = () => {
    if (disabled) return
    if (!orderId) {
      message.error("Select exactly one order to file a complaint.")
      return
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    resetForm()
  }

  // Get the next complaint number safely: counters/complaints.next
  async function getNextComplaintNumber() {
    const counterRef = doc(db, "counters", "complaints")
    const nextNumber = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef)
      let current = 0
      if (snap.exists()) {
        current = snap.data()?.next || 0
      } else {
        tx.set(counterRef, { next: 0 })
      }
      const newVal = current + 1
      tx.update(counterRef, { next: newVal })
      return newVal
    })
    return nextNumber
  }

  const saveComplaint = async () => {
    if (!comment.trim()) {
      message.warning("Please write a short comment.")
      return
    }
    if (!orderId) {
      message.error("Missing order id.")
      return
    }

    try {
      setSaving(true)

      // 1) Reserve next human-friendly id
      const nextNum = await getNextComplaintNumber()
      const humanId = `#COMP${nextNum}`

      // 2) Build complaint payload (keep full order snapshot)
      const payload = {
        humanId, // "#COMP12"
        orderId, // reference order id
        orderNumber, // reference order number
        orderSnapshot: orderData, // <-- full order as-is
        type, // "Count" | "Quality" | "Error" | "Other"
        responsibility, // "Viva" | "Arsoma" | ...
        comment: comment.trim(),
        status: "In review",
        createdAt: serverTimestamp(),
        createdByUid: user?.uid || null,
        createdByEmail: user?.email || null,
      }

      // 3) Write complaint + mark order as "in review" in one batch
      const batch = writeBatch(db)
      const complaintsCol = collection(db, "complaints")
      const complaintRef = doc(complaintsCol) // create a new doc id

      batch.set(complaintRef, payload)
      batch.update(doc(db, "orders", orderId), {
        complaintStatus: "In review",
      })

      await batch.commit()

      message.success(`Complaint ${humanId} created and order updated.`)
      handleClose()
    } catch (err) {
      console.error(err)
      message.error("Failed to create complaint.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button onClick={handleOpen} danger icon={<CloseOutlined />} disabled={disabled}>
        {buttonText}
      </Button>

      <Drawer
        width={520}
        title={`New Complaint — ${orderNumber || ""}`}
        open={open}
        onClose={handleClose}
        extra={
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={saveComplaint}>
              Save
            </Button>
          </Space>
        }>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text type="secondary">Complaint Type</Text>
            <Select
              style={{ width: "100%", marginTop: 6 }}
              value={type}
              onChange={setType}
              options={complaintTypes.map((t) => ({ value: t, label: <span>{t}</span> }))}
            />
          </div>

          <div>
            <Text type="secondary">Responsibility</Text>
            <Select
              style={{ width: "100%", marginTop: 6 }}
              value={responsibility}
              onChange={setResponsibility}
              options={responsibilities.map((r) => ({ value: r, label: <span>{r}</span> }))}
            />
          </div>

          <div>
            <Text type="secondary">Issue</Text>
            <TextArea
              rows={5}
              placeholder="What went wrong?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>
        </Space>
      </Drawer>
    </>
  )
}
